import React, { useState } from 'react';
import { useWallet } from '../hooks/useWallet';

const Header = () => {
  const { 
    address, 
    isConnected, 
    connectWallet, 
    disconnect, 
    ensName, 
    ensAvatar 
  } = useWallet();
  const [showWalletMenu, setShowWalletMenu] = useState(false);

  const truncateAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const displayName = ensName || truncateAddress(address);

  return (
    <header className="bg-dominant text-white sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
              <i className="fas fa-shield-alt text-white text-xl"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold">SafeDash</h1>
              <p className="text-secondary text-sm">Decentralized DAO Dashboard</p>
            </div>
          </div>
          
          <nav className="hidden md:block">
            <ul className="flex space-x-8">
              {[
                { icon: 'home', label: 'Home' },
                { icon: 'users', label: 'DAOs' },
                { icon: 'chart-line', label: 'DeFi' },
                { icon: 'wallet', label: 'Wallet' },
                { icon: 'cog', label: 'Settings' }
              ].map((item, index) => (
                <li key={index}>
                  <a 
                    href="#" 
                    className="flex items-center gap-2 font-medium hover:text-secondary transition-colors duration-200"
                  >
                    <i className={`fas fa-${item.icon}`}></i>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="relative">
            {!isConnected ? (
              <button 
                onClick={() => connectWallet('metaMask')}
                className="bg-secondary hover:bg-accent text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              >
                <i className="fas fa-wallet"></i>
                Connect Wallet
              </button>
            ) : (
              <div className="relative">
                <button 
                  onClick={() => setShowWalletMenu(!showWalletMenu)}
                  className="bg-secondary hover:bg-accent text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  {ensAvatar ? (
                    <img 
                      src={ensAvatar} 
                      alt="ENS Avatar" 
                      className="w-6 h-6 rounded-full"
                      onError={(e) => {
                        // Fallback to icon if avatar fails to load
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <i className="fas fa-user-circle"></i>
                  )}
                  
                  <span className="max-w-[120px] truncate">
                    {displayName}
                  </span>
                  
                  {ensName && (
                    <span className="bg-accent text-xs px-1 py-0.5 rounded" title="ENS Verified">
                      ✓
                    </span>
                  )}
                  
                  <i className={`fas fa-chevron-${showWalletMenu ? 'up' : 'down'} text-sm`}></i>
                </button>
                
                {showWalletMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    {/* Wallet Info */}
                    <div className="px-4 py-2 border-b border-gray-100">
                      <div className="flex items-center gap-3 mb-2">
                        {ensAvatar ? (
                          <img 
                            src={ensAvatar} 
                            alt="ENS Avatar" 
                            className="w-8 h-8 rounded-full"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <div className={`w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center ${ensAvatar ? 'hidden' : 'flex'}`}>
                          <i className="fas fa-user text-gray-500"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {ensName || 'No ENS'}
                          </p>
                          <p className="text-xs text-gray-500 font-mono truncate">
                            {truncateAddress(address)}
                          </p>
                        </div>
                      </div>
                      
                      {ensName && (
                        <div className="flex items-center gap-1 text-xs text-green-600">
                          <i className="fas fa-check-circle"></i>
                          ENS Resolved
                        </div>
                      )}
                    </div>

                    {/* Menu Items */}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(address);
                        setShowWalletMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <i className="fas fa-copy"></i>
                      Copy Address
                    </button>
                    
                    <a
                      href={`https://etherscan.io/address/${address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      onClick={() => setShowWalletMenu(false)}
                    >
                      <i className="fas fa-external-link-alt"></i>
                      View on Etherscan
                    </a>
                    
                    {ensName && (
                      <a
                        href={`https://app.ens.domains/${ensName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        onClick={() => setShowWalletMenu(false)}
                      >
                        <i className="fas fa-address-card"></i>
                        View ENS Profile
                      </a>
                    )}

                    <div className="border-t border-gray-100 my-1"></div>
                    
                    <button
                      onClick={() => {
                        disconnect();
                        setShowWalletMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <i className="fas fa-sign-out-alt"></i>
                      Disconnect
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;